import React, { useState, useEffect, useCallback, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { ChatMessages } from './ChatMessages';
import { ChatMessage } from '../../entities/ChatMessage';
import { RecommendationItem } from '../../types';
import { apiService } from '../../services/api';
import { useTeam } from '../../contexts/TeamContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

const ChatTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1rem;
  background: transparent;
  border-radius: 1.5rem;
`;

const ChatMessagesContainer = styled.div`
  flex: 1;
  overflow: hidden;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: transparent;
  border-radius: 1rem;
  border: none;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ChatInputForm = styled.form`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  background: transparent;
  border-radius: 1rem;
  border: none;
`;

const ChatInputField = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: 0.75rem;
  color: var(--color-text-primary);
  font-size: 0.95rem;
  font-family: var(--font-family-sans);
  outline: none;
  transition: all 0.2s var(--transition-smooth);
  
  &:focus {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.1);
  }
  
  &::placeholder {
    color: var(--color-text-secondary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ChatSendButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-light));
  color: #ffffff;
  border: none;
  border-radius: 0.75rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s var(--transition-smooth);
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ChatClearButton = styled.button`
  padding: 0.75rem 1rem;
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  color: var(--color-text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s var(--transition-smooth);
  
  &:hover:not(:disabled) {
    background: var(--color-surface);
    border-color: var(--color-border-strong);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface RecommendationsChatProps {
  offensiveRecommendations: RecommendationItem[];
  defensiveRecommendations: RecommendationItem[];
  isLoading: boolean;
}

// Utility function to format recommendation items into readable strings
const formatRecommendation = (recommendation: RecommendationItem, type: 'offensive' | 'defensive'): string => {
  const typePrefix = type === 'offensive' ? '⚡ Offensive' : '🛡 Defensive';
  return `**${typePrefix} - ${recommendation.focus_area}**: ${recommendation.coaching_suggestion}. ${recommendation.supporting_data}. ${recommendation.why_it_matters}`;
};

export const RecommendationsChat: React.FC<RecommendationsChatProps> = ({
  offensiveRecommendations,
  defensiveRecommendations,
  isLoading
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const { matchupConfig, selectedLeague } = useTeam();

  // Store recommendations as context (not displayed in UI)
  const [recommendationsContext, setRecommendationsContext] = useState<ChatMessage[]>([]);

  // Initialize recommendations context when recommendations change
  useEffect(() => {
    if (offensiveRecommendations.length > 0 || defensiveRecommendations.length > 0) {
      const contextMessages: ChatMessage[] = [];
      
      // Add offensive recommendations as user messages for context
      offensiveRecommendations.forEach((rec, index) => {
        contextMessages.push({
          id: `offensive-${index}-${Date.now()}`,
          content: formatRecommendation(rec, 'offensive'),
          role: 'user',
          timestamp: new Date()
        });
      });
      
      // Add defensive recommendations as user messages for context
      defensiveRecommendations.forEach((rec, index) => {
        contextMessages.push({
          id: `defensive-${index}-${Date.now()}`,
          content: formatRecommendation(rec, 'defensive'),
          role: 'user',
          timestamp: new Date()
        });
      });
      
      setRecommendationsContext(contextMessages);
      // Clear visible messages when recommendations change
      setMessages([]);
    }
  }, [offensiveRecommendations, defensiveRecommendations]);

  const sendMessage = useCallback(async (content: string) => {
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content,
      role: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsChatLoading(true);
    setError(null);

    try {
      // Build message history: recommendations context + visible messages
      const history = [
        // Add recommendations context first
        ...recommendationsContext.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        // Add visible chat messages
        ...messages.map(msg => ({
          role: msg.role,
          content: msg.content
        }))
      ];

      // Build match_config
      const matchConfig = {
        stage: matchupConfig?.matchup_type || "regular",
        userTeam: {
          teamId: matchupConfig?.user_team?.teamId || '',
          teamName: matchupConfig?.user_team?.teamName || ''
        },
        opponentTeam: {
          teamId: matchupConfig?.opponent_team?.teamId || '',
          teamName: matchupConfig?.opponent_team?.teamName || ''
        },
        seasonId: matchupConfig?.season || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`,
        league: selectedLeague?.abbreviation || "NBA"
      };

      // Build ChatInput object
      const chatInput = {
        message: content,
        history,
        match_config: matchConfig
      };

      const response = await apiService.sendChatMessage(chatInput);
      
      if (response.response) {
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: response.response,
          role: 'assistant',
          timestamp: new Date()
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        setError('No response received from server');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(errorMessage);
    } finally {
      setIsChatLoading(false);
    }
  }, [messages, recommendationsContext, matchupConfig]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    // Note: recommendationsContext is preserved to maintain context
  }, []);

  // Don't render if recommendations are still loading
  if (isLoading) {
    return null;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isChatLoading) {
      sendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleClear = () => {
    if (!isChatLoading) {
      clearChat();
      setInputValue('');
    }
  };

  const welcomeMessage = "I have context about all the recommendations above. Ask me anything about them!";

  return (
    <ChatTabContainer>
      <ChatMessagesContainer>
        <ChatMessages 
          messages={messages} 
          isLoading={isChatLoading}
          customWelcomeMessage={welcomeMessage}
          showWelcomeMessage={true}
        />
      </ChatMessagesContainer>
      <ChatInputForm onSubmit={handleSubmit}>
        <ChatInputField
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Ask about these recommendations..."
          disabled={isChatLoading}
        />
        {messages.length > 0 && (
          <ChatClearButton
            type="button"
            onClick={handleClear}
            disabled={isChatLoading}
            title="Clear chat history"
          >
            <FontAwesomeIcon icon={faTrash} />
          </ChatClearButton>
        )}
        <ChatSendButton
          type="submit"
          disabled={!inputValue.trim() || isChatLoading}
        >
          {isChatLoading ? (
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          ) : (
            '→'
          )}
        </ChatSendButton>
      </ChatInputForm>
    </ChatTabContainer>
  );
};
